/**
 * Test Logs API Service
 * 
 * Centralized API functions for test logs data fetching.
 * Handles three types of test executions:
 * - Single API executions
 * - Orchestration executions
 * - Load test executions
 * 
 * Uses the shared API utility for consistent error handling and authentication.
 */

import api from '../../../shared/utils/api';
import { buildResultParams } from '../utils/buildResultParams';

/**
 * Fetch paginated list of test execution logs
 * 
 * @param {string} date - Start (or single) date in YYYY-MM-DD format
 * @param {string} endDate - Optional end date in YYYY-MM-DD format ('' or undefined for single-day)
 * @param {number} page - Page number (0-indexed)
 * @param {number} size - Number of items per page
 * @param {string} localTimeZone - Local time zone (e.g., 'America/New_York')
 * @param {string} serverTimeZone - Server time zone (e.g., 'UTC')
 * @param {string} type - Optional type filter ('SINGLE_API', 'ORCHESTRATION', 'LOAD_TEST', or 'ALL')
 * @returns {Promise<Object>} Object containing logs array, total_pages, and size
 * @throws {Error} If the API request fails
 * 
 * @example
 * const result = await fetchLogs('2026-01-09', '', 0, 20, 'America/New_York', 'UTC', 'SINGLE_API');
 * // Returns: { logs: [...], total_pages: 5, size: 20 }
 */
export const fetchLogs = async (date, endDate, page, size, localTimeZone, serverTimeZone, type = 'ALL') => {
  try {
    // Assemble the request params via the shared pure helper. It always
    // includes date/page/size/localTimeZone/serverTimeZone, adds `type` only
    // when not 'ALL', and adds `endDate` only for a true multi-day range (DD1).
    const params = buildResultParams({
      date,
      endDate,
      page,
      size,
      localTimeZone,
      serverTimeZone,
      type,
    });

    const response = await api.get('/api/result', { params });
    
    // Handle different response structures
    // Backend may return logs directly or nested in a data property
    const logs = response?.logs || response?.data?.logs || [];
    const totalPages = response?.total_pages || response?.data?.total_pages || 1;
    const pageSize = response?.size || response?.data?.size || size;
    const total = response?.total || response?.data?.total || 0;
    
    // Type-wise total counts for the entire day (from backend)
    const totalSingleApi = response?.total_single_api || response?.data?.total_single_api || 0;
    const totalOrchestration = response?.total_orchestration || response?.data?.total_orchestration || 0;
    const totalLoadTest = response?.total_load_test || response?.data?.total_load_test || 0;
    const totalAdvancedOrchestration = response?.total_advanced_orchestration || response?.data?.total_advanced_orchestration || 0;
    
    return {
      logs,
      total_pages: totalPages,
      size: pageSize,
      total,
      // Type counts for the entire day
      total_single_api: totalSingleApi,
      total_orchestration: totalOrchestration,
      total_load_test: totalLoadTest,
      total_advanced_orchestration: totalAdvancedOrchestration,
    };
  } catch (error) {
    console.error('Error fetching test logs:', error);
    throw new Error('Failed to fetch test logs. Please try again later.');
  }
};

/**
 * Fetch single API execution details
 * 
 * Returns detailed information about a single API test execution,
 * including request/response data for all executions of that configuration.
 * 
 * @param {string} configId - The configuration ID of the single API execution
 * @returns {Promise<Object>} Object containing scheduled_executions array and name
 * @throws {Error} If the API request fails
 * 
 * @example
 * const details = await fetchSingleApiDetails('config-123');
 * // Returns: { scheduled_executions: [...], name: 'API Test Name' }
 */
export const fetchSingleApiDetails = async (configId) => {
  try {
    const response = await api.get(`/api/result/execution/${configId}`);
    
    // Return the response as-is since it should already be in the correct format
    // Expected structure: { scheduled_executions: [...], name: string }
    return response;
  } catch (error) {
    console.error('Error fetching single API details:', error);
    throw new Error('Failed to fetch API execution details. Please try again later.');
  }
};

/**
 * Fetch orchestration execution details
 * 
 * Returns detailed information about an orchestration execution,
 * including all step executions with their request/response data.
 * 
 * @param {string} configId - The configuration ID of the orchestration execution
 * @returns {Promise<Object>} Object containing scheduled_executions array with step_executions
 * @throws {Error} If the API request fails
 * 
 * @example
 * const details = await fetchOrchestrationDetails('orch-456');
 * // Returns: { scheduled_executions: [{ step_executions: [...], ... }] }
 */
export const fetchOrchestrationDetails = async (configId) => {
  try {
    const response = await api.get(`/api/result/schedule/orchestration/single/${configId}`);
    
    // Return the response as-is since it should already be in the correct format
    // Expected structure: { scheduled_executions: [{ step_executions: [...] }] }
    return response;
  } catch (error) {
    console.error('Error fetching orchestration details:', error);
    throw new Error('Failed to fetch orchestration execution details. Please try again later.');
  }
};

/**
 * Fetch load test execution details
 * 
 * Returns performance metrics and configuration for a load test execution.
 * Transforms the response to flatten the first execution's data to the root level
 * for easier consumption by components.
 * 
 * @param {string} configId - The configuration ID of the load test execution
 * @returns {Promise<Object>} Object containing flattened load test metrics
 * @throws {Error} If the API request fails
 * 
 * @example
 * const metrics = await fetchLoadTestDetails('load-789');
 * // Returns: { avg_response_time_ms: 150, total_requests: 1000, ... }
 */
export const fetchLoadTestDetails = async (configId) => {
  try {
    const response = await api.get(`/api/result/load-test/${configId}`);
    
    // Transform response to flatten the first execution's data
    // Backend returns: { scheduled_executions: [{ metrics... }] }
    // We flatten to: { metrics... } for easier component consumption
    
    if (!response?.scheduled_executions || response.scheduled_executions.length === 0) {
      throw new Error('No load test execution data found');
    }
    
    const firstExecution = response.scheduled_executions[0];
    
    // Return flattened structure with all metrics at root level
    return {
      avg_bytes_received: firstExecution.avg_bytes_received,
      avg_bytes_sent: firstExecution.avg_bytes_sent,
      avg_response_time_ms: firstExecution.avg_response_time_ms,
      failed_requests: firstExecution.failed_requests,
      max_response_time_ms: firstExecution.max_response_time_ms,
      min_response_time_ms: firstExecution.min_response_time_ms,
      p50_response_time_ms: firstExecution.p50_response_time_ms,
      p90_response_time_ms: firstExecution.p90_response_time_ms,
      p95_response_time_ms: firstExecution.p95_response_time_ms,
      p99_response_time_ms: firstExecution.p99_response_time_ms,
      requests_per_second: firstExecution.requests_per_second,
      steady_state_throughput: firstExecution.steady_state_throughput,
      steady_state_duration_second: firstExecution.steady_state_duration_second,
      successful_requests: firstExecution.successful_requests,
      total_duration_second: firstExecution.total_duration_second,
      total_execution_time_ms: firstExecution.total_execution_time_ms,
      actual_screen_time_second: firstExecution.actual_screen_time_second,
      total_requests: firstExecution.total_requests,
      execution_config_id: firstExecution.execution_config_id,
      request_name: firstExecution.name,
      environment_name: firstExecution.environment_name,
      dataset_name: firstExecution.dataset_name,
      concurrency: firstExecution.concurrency,
      iterations: firstExecution.iterations,
      test_duration: firstExecution.test_duration
    };
  } catch (error) {
    console.error('Error fetching load test details:', error);
    throw new Error('Failed to fetch load test execution details. Please try again later.');
  }
};

/**
 * Fetch all advanced orchestration executions across all flows for the user's org.
 * Lists all flows via GET /api/advanced-orchestration/org, then fetches execution
 * history for each flow via GET /api/advanced-orchestration/{id}/executions.
 * 
 * @returns {Promise<Array>} Flat array of execution result DTOs across all flows
 */
export const fetchAdvancedOrchestrationExecutions = async () => {
  try {
    // Step 1: Get all flows for the org
    const flows = await api.get('/api/advanced-orchestration/org');
    const flowList = Array.isArray(flows) ? flows : (flows?.flows || []);

    if (flowList.length === 0) {
      return [];
    }

    // Step 2: Fetch execution history for each flow in parallel
    const executionPromises = flowList.map(async (flow) => {
      try {
        const executions = await api.get(`/api/advanced-orchestration/${flow.id}/executions`);
        return Array.isArray(executions) ? executions : [];
      } catch {
        // If a single flow's executions fail, skip it
        return [];
      }
    });

    const allExecutions = await Promise.all(executionPromises);
    // Flatten and sort by startTime descending
    return allExecutions
      .flat()
      .sort((a, b) => {
        const timeA = a.startTime ? new Date(a.startTime).getTime() : 0;
        const timeB = b.startTime ? new Date(b.startTime).getTime() : 0;
        return timeB - timeA;
      });
  } catch (error) {
    console.error('Error fetching advanced orchestration executions:', error);
    throw new Error('Failed to fetch advanced orchestration executions.');
  }
};

/**
 * Fetch manual (non-scheduled) advanced orchestration executions for a date or range.
 * Uses the dedicated backend endpoint that filters by date and excludes scheduled runs.
 *
 * @param {string} date - Start (or single) date in YYYY-MM-DD format
 * @param {string} endDate - Optional end date in YYYY-MM-DD format ('' or undefined for single-day)
 * @returns {Promise<Array>} Array of ExecutionResultDTO summaries
 */
export const fetchManualAdvancedOrchestrationExecutions = async (date, endDate) => {
  try {
    const params = { date };
    // Only add endDate for a true multi-day range: a non-empty end date that
    // differs from the start date (same "true range only" rule as buildResultParams, DD1).
    if (typeof endDate === 'string' && endDate !== '' && endDate !== date) {
      params.endDate = endDate;
    }
    const response = await api.get('/api/advanced-orchestration/executions/manual', {
      params,
    });
    return response?.executions || [];
  } catch (error) {
    console.error('Error fetching manual advanced orchestration executions:', error);
    throw new Error('Failed to fetch manual advanced orchestration executions.');
  }
};

/**
 * Fetch detailed execution result for a specific advanced orchestration execution.
 *
 * @param {number} execId - Execution ID
 * @returns {Promise<Object>} ExecutionResultDTO with nodeResults and iterations
 */
export const fetchAdvancedOrchestrationExecutionDetails = async (execId) => {
  try {
    const response = await api.get(`/api/advanced-orchestration/execution/${execId}`);
    return response?.execution || response;
  } catch (error) {
    console.error('Error fetching advanced orchestration execution details:', error);
    throw new Error('Failed to fetch execution details.');
  }
};

/**
 * Fetch per-node results for a specific iteration of an advanced orchestration execution.
 *
 * @param {number} execId - Execution ID
 * @param {number} iterationIndex - Iteration index (0-based)
 * @returns {Promise<Object>} ExecutionResultDTO with iteration-specific nodeResults
 */
export const fetchAdvancedOrchestrationIterationDetails = async (execId, iterationIndex) => {
  try {
    const response = await api.get(`/api/advanced-orchestration/execution/${execId}/iteration/${iterationIndex}`);
    return response?.execution || response;
  } catch (error) {
    console.error('Error fetching iteration details:', error);
    throw new Error('Failed to fetch iteration details.');
  }
};

/**
 * Fetch per-node results for a specific dataset row within an iteration.
 *
 * @param {number} execId - Execution ID
 * @param {number} iterationIndex - Iteration index (0-based)
 * @param {number} rowIndex - Dataset row index (0-based)
 * @returns {Promise<Object>} ExecutionResultDTO with row-specific nodeResults
 */
export const fetchAdvancedOrchestrationRowDetails = async (execId, iterationIndex, rowIndex) => {
  try {
    const response = await api.get(`/api/advanced-orchestration/execution/${execId}/iteration/${iterationIndex}/row/${rowIndex}`);
    return response?.execution || response;
  } catch (error) {
    console.error('Error fetching row details:', error);
    throw new Error('Failed to fetch row details.');
  }
};

/**
 * Export all API functions as a single object for convenience
 */
export const testLogsApi = {
  fetchLogs,
  fetchSingleApiDetails,
  fetchOrchestrationDetails,
  fetchLoadTestDetails,
  fetchAdvancedOrchestrationExecutions,
  fetchManualAdvancedOrchestrationExecutions,
  fetchAdvancedOrchestrationExecutionDetails,
  fetchAdvancedOrchestrationIterationDetails,
  fetchAdvancedOrchestrationRowDetails,
};

export default testLogsApi;
